import { isObjectLike } from './objects';

type StringKeyedRecord = Record<string, unknown>;

export function operation<T extends StringKeyedRecord>(
  list1: T[],
  list2: T[],
  compareProp: keyof T & string,
  isUnion = false
): T[] {
  return list1.filter(
    (a) => isUnion === list2.some((b) => a[compareProp] === b[compareProp])
  );
}

export function inBoth<T extends StringKeyedRecord>(
  list1: T[],
  list2: T[],
  compareProp: keyof T & string
): T[] {
  return operation(list1, list2, compareProp, true);
}
export const inFirstOnly = operation;

export function inSecondOnly<T extends StringKeyedRecord>(
  list1: T[],
  list2: T[],
  compareProp: keyof T & string
) {
  return inFirstOnly(list2, list1, compareProp);
}

export function swapArrayElements<T>(
  arr: T[],
  currentIndex: number,
  nextIndex: number
) {
  const temp = arr[currentIndex];
  arr[currentIndex] = arr[nextIndex];
  arr[nextIndex] = temp;
}

function getNestedValue(item: StringKeyedRecord, path: string): unknown {
  return path.split('.').reduce<unknown>((currentValue, segment) => {
    if (!isObjectLike(currentValue)) {
      return undefined;
    }

    return currentValue[segment];
  }, item);
}

export function uniqueObjectsByFields<T extends StringKeyedRecord>(
  collection: T[],
  fields: string[] = []
) {
  const uniqueItems: T[] = [];
  const map = new Map();
  for (const item of collection) {
    if (item) {
      let key = '';
      fields.forEach((f) => {
        if (f.indexOf('.') > -1) {
          key += `-${getNestedValue(item, f) || ''}`;
        } else {
          key += `-${item[f] || ''}`;
        }
      });
      // debug unique key:
      // console.log('uniqueObjectsByFields key:', key);
      if (!map.has(key)) {
        map.set(key, true);
        uniqueItems.push(item);
      }
    }
  }
  return uniqueItems;
}

export function uniqueObjectsBySimpleFields<T>(
  collection: T[],
  fields: Array<keyof T> = []
) {
  const uniqueItems: T[] = [];
  const set = new Set();
  for (const item of collection) {
    if (item) {
      let key = '';
      fields.forEach((f) => {
        key += `-${item[f] || ''}`;
      });
      // debug unique key:
      // console.log('uniqueObjectsByFields key:', key);
      if (!set.has(key)) {
        set.add(key);
        uniqueItems.push(item);
      }
    }
  }
  return uniqueItems;
}

/**
 * Gets all unique items from a collection using a projection function to get a key.
 * @param collection Array to be unique'd
 * @param project Function to project each item to a key. toString is used to map the return to a string.
 * @returns An Array‹T› containing unique items from collection
 */
export function uniqueObjectsByProjection<T>(
  collection: T[],
  project: (item: T) => unknown
) {
  const uniqueItems: T[] = [];
  const set = new Set();
  for (const item of collection) {
    if (item) {
      const key = String(project(item));
      if (!set.has(key)) {
        set.add(key);
        uniqueItems.push(item);
      }
    }
  }
  return uniqueItems;
}

export function uniqueArray(collection: Array<string | number>) {
  // only works for primitive values in collection (string, number, etc)
  // if using objects use above method
  return [...new Set(collection)];
}

/**
 * Sort collection by a date property in descending order
 * @param sortBy object property/field containing date string to sort by
 */
export function sortByDate<TKey extends string>(
  sortBy: TKey,
  ascending?: boolean
) {
  return function <TItem>(a: TItem, b: TItem) {
    const valueA = (a as Record<string, unknown>)[sortBy];
    const valueB = (b as Record<string, unknown>)[sortBy];
    if (!valueA) {
      // console.log(`sortByDate: '${sortBy}' is falsey.`);
      return -1;
    }
    // console.log(`sortByDate: '${sortBy}' is ${a[sortBy]}`);
    const dateA = new Date(valueA as string | number | Date);
    const dateB = new Date(valueB as string | number | Date);
    if (ascending) {
      return dateB < dateA ? 1 : -1;
    } else {
      return dateA > dateB ? -1 : 1;
    }
  };
}

/**
 * Example usage: array.sort(sortByProperty('Name'))
 * Sort collection by specified property in descending order
 * @param sortBy object property/field to sort by
 */
export function sortByProperty<TKey extends string>(
  sortBy: TKey,
  ascending?: boolean
) {
  return function <TItem extends { [Key in TKey]?: unknown }>(
    a: TItem,
    b: TItem
  ) {
    const valueA = a[sortBy];
    const valueB = b[sortBy];
    if (valueA === null || valueA === undefined) {
      console.log(`sortByProperty: '${sortBy}' is null or undefined.`);
      return -1;
    }
    // console.log(`sortByProperty: '${sortBy}' is ${a[sortBy]}`);
    if (ascending) {
      return valueB < valueA ? 1 : -1;
    } else {
      return valueA > valueB ? -1 : 1;
    }
  };
}

/**
 * Meant to be used with multi-column-search directive
 * @param query the query object
 * @param dataToFilter the collection to filter
 */
export function mscFilterResults(
  query: { value: unknown; operator: string; field: string },
  dataToFilter: StringKeyedRecord[]
): { filterValue?: unknown; matches?: StringKeyedRecord[] } {
  console.log('query:', query);
  const filterValue =
    query.value && typeof query.value === 'string'
      ? query.value.toLowerCase()
      : query.value;
  return {
    filterValue,
    // first filter always filters first the entire set
    // additional filters apply to remaining results
    matches: dataToFilter.filter((i) => {
      if (query.operator === 'contains' && filterValue) {
        const fieldValue = String(i[query.field] ?? '');
        return fieldValue.toLowerCase().indexOf(filterValue as string) > -1;
      } else if (query.operator === 'equals') {
        const fieldValue = i[query.field];
        if (typeof fieldValue === 'string') {
          return fieldValue.toLowerCase() === filterValue;
        } else {
          return fieldValue === filterValue;
        }
      } else if (query.operator === 'isNotNull') {
        return !!i[query.field];
      } else if (query.operator === 'isNull') {
        return !i[query.field];
      }
      return false;
    }),
  };
}

/**
 * Filter an array into two separate arrays
 * @param array Array to be filtered
 * @param callback Function to filter by
 * @returns [[], []] where the first [] is true
 */
export function partition<T>(
  array: T[],
  callback: (el: T, idx?: number, array?: T[]) => boolean
): [T[], T[]] {
  return array.reduce(
    function (result, element, i) {
      if (callback(element, i, array)) {
        result[0].push(element);
      } else {
        result[1].push(element);
      }

      return result;
    },
    [[], []] as [T[], T[]]
  );
}

/**
 * Pick N random items from an array
 * @param array Array to pick items from
 * @param n Number of items to pick
 * @returns New Array‹T›
 */
export function sample<T>(array: T[], n: number): T[] {
  const results: T[] = [];
  const copy = [...array];
  while (results.length < n) {
    const idx = Math.floor(Math.random() * copy.length);
    results.push(copy[idx]);
    copy.splice(idx, 1);
  }
  return results;
}

/**
 * Cached-wins deep merge of arrays
 */
type KeyFn<T> = (item: T) => string;

export interface ArrayMergeSchema {
  [fieldName: string]: {
    key: KeyFn<unknown>;
    children?: ArrayMergeSchema;
  };
}

export function mergeObjectsCachedPriorityMut(
  cached: StringKeyedRecord,
  remote: StringKeyedRecord,
  schema: ArrayMergeSchema = {}
): StringKeyedRecord {
  if (remote === undefined) return cached;
  if (!isObjectLike(cached) || !isObjectLike(remote)) {
    // Primitive or mismatched: cached already wins; nothing to do.
    return cached;
  }

  // Add remote-only keys & merge arrays/objects. Cached keys are kept as-is.
  for (const k of Object.keys(remote)) {
    const rVal = remote[k];
    const cHas = Object.prototype.hasOwnProperty.call(cached, k);

    const arrCfg = schema[k];
    if (arrCfg && (Array.isArray(rVal) || Array.isArray(cached[k]))) {
      const cArr = Array.isArray(cached[k])
        ? cached[k]
        : ((cached[k] = []) as unknown[]);
      const rArr = Array.isArray(rVal) ? rVal : [];
      mergeArraysCachedPriorityMut(
        cArr,
        rArr,
        arrCfg.key,
        arrCfg.children ?? {}
      );
      continue;
    }

    if (isObjectLike(rVal)) {
      if (cHas && isObjectLike(cached[k])) {
        mergeObjectsCachedPriorityMut(
          cached[k],
          rVal,
          schema[k]?.children ?? {}
        );
      } else if (!cHas) {
        cached[k] = rVal; // bring over remote-only nested object
      }
      continue;
    }

    // Primitive: only copy if cache doesn't have it.
    if (!cHas) cached[k] = rVal;
  }

  return cached;
}

export function mergeArraysCachedPriorityMut<T>(
  cachedArr: T[],
  remoteArr: T[],
  keyFn: (t: T) => string,
  childrenSchema: ArrayMergeSchema
): T[] {
  // Index existing cached items by key for O(1) lookups.
  const index = new Map<string, number>();
  for (let i = 0; i < cachedArr.length; i++) {
    index.set(keyFn(cachedArr[i]), i);
  }

  // Record which remote keys we processed (handles deletes + guards against remote dups).
  const seen = new Set<string>();

  // Upsert: merge existing or append truly new.
  for (const r of remoteArr) {
    const id = keyFn(r);
    if (seen.has(id)) continue; // ignore duplicate keys in remote list
    seen.add(id);

    const pos = index.get(id);
    if (pos !== undefined) {
      // Merge remote into cached with cached priority.
      const cachedValue = cachedArr[pos];
      if (isObjectLike(cachedValue) && isObjectLike(r)) {
        mergeObjectsCachedPriorityMut(cachedValue, r, childrenSchema);
      } else {
        cachedArr[pos] = r;
      }
    } else {
      // New item: append and remember its index.
      index.set(id, cachedArr.length);
      cachedArr.push(r);
    }
  }

  /**
   * Be very careful with this.
   * Most likely we need an option to pass in on which particular array (top level or nested) we want to prune.
   */
  // Prune: remove any cached items not present in remote (based on keyFn).
  // Iterate backwards so splices don't affect yet-to-visit indices.
  // for (let i = cachedArr.length - 1; i >= 0; i--) {
  //   const id = keyFn(cachedArr[i]);
  //   if (!seen.has(id)) {
  //     cachedArr.splice(i, 1);
  //   }
  // }

  return cachedArr; // mutated
}
